import React, { useReducer } from 'react';
import styled from '@emotion/styled';
import Span from '../../text/Span';
import NumberInput from '../../input/NumberInput';
import inputReducer from '../../../../reducer/input-reducer';
import Input from '../../input/Input';
import { LIGHT_BLUE, ORANGE, WHITE } from '../../../../styles/constants/color';
import CustomSelect from '../../select/CustomSelect';
import Title from '../../text/Title';
import { InstallmentCalculator } from '../../../../types/estimate/input/Input';
import { monthlyPriceCalculator } from '../../../../lib/calculationFormula';
import CountNumber from '../../CountNumber';
import { transformObjToSimpleItem } from '../../../../lib/refineObj';
import AlertModal from '../../modal/AlertModal';
import useSwitching from '../../../../hooks/common/useSwitching';
import { MODAL_PADDING_HOR } from '../../../../styles/constants/size';
import { flexFrame } from '../../../../styles/mixin/figure';
import SimpleButton from '../../button/SimpleButton';
import SimpleListModal from '../../modal/SimpleListModal';

const Wrapper = styled.div`
  width: 310px;
  height: auto;
  padding: 0 35px;
  text-align: center;

  & > button:first-of-type {
    margin-right: 8px;
  }

  @media (max-width: calc(310px + ${MODAL_PADDING_HOR} + ${MODAL_PADDING_HOR})) {
    width: 78vw;
  }
`;

const Dl = styled.dl`
  width: 100%;
  height: auto;
  margin-bottom: 20px;
`;

const Item = styled.div`
  ${flexFrame('', 'space-between', 'center')};
  width: 100%;
  height: 35px;
`;

const Dt = styled.dt`
  width: auto;
  height: auto;
`;

const Dd = styled.dd`
  width: auto;
  height: auto;
`;

/**
 *  header의 할부계산기 컴포넌트
 *  */
function Calculator(): JSX.Element {
  const [info, dispatch] = useReducer(inputReducer<InstallmentCalculator>, INIT_STATE);
  const [modalActive, modalSwitching]: [boolean, () => void] = useSwitching();
  const [alertActive, alertSwitching]: [boolean, () => void] = useSwitching();

  /**
   *  info에 직접 업데이트하는 함수
   * @param {string} name 키 이름
   * @param {string | number} value 값
   *  */
  const targetUpdate = (name: string, value: string | number): void => {
    const target: any = {
      type: 'self',
      name,
      value,
    };

    dispatch({
      type: 'OBJECT',
      target,
    });
  };

  /**
   * @descrption 금리 클릭 이벤트
   * @param {number | string} param 금리 혹은 직접입력
   *  */
  const onClick = (param: number | string): void => targetUpdate('term', param);

  /**
   * @descrption input의 onChange 공통 함수.
   * @param {React.ChangeEvent<HTMLInputElement>} e 이벤트 객체
   *  */
  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const target: EventTarget & HTMLInputElement = e.target;
    dispatch({ type: 'OBJECT', target });
  };

  /**
   *  계산하기 버튼 클릭 함수. 월납입금을 계산한다.
   *  */
  const calculate = (): void => {
    const { basePrice, term, termSelf, percentage } = info;

    if ((term === '직접입력' && !termSelf) || !percentage) {
      alertSwitching();
      return;
    }

    const returnPrice: number = monthlyPriceCalculator(
      basePrice,
      term === '직접입력' ? termSelf : Number(term),
      percentage
    );

    targetUpdate('monthlyPrice', returnPrice);
  };

  /**
   *  초기화 버튼 클릭 이벤트. 모든 값을 초기값으로 설정한다.
   *  */
  const init = (): void =>
    dispatch({
      type: 'INIT',
      target: {
        value: INIT_STATE,
      },
    });

  return (
    <>
      <Wrapper>
        <Title text='할부계산기' />
        <Dl>
          <Item>
            <Dt>
              <Span fontSize='15px'>할부원금</Span>
            </Dt>
            <Dd>
              <NumberInput
                name='basePrice'
                width='100px'
                onChange={onChange}
                value={info.basePrice}
              />
              <Span> 원</Span>
            </Dd>
          </Item>
          <Item>
            <Dt>
              <Span fontSize='15px'>할부기간</Span>
            </Dt>
            <Dd>
              <CustomSelect
                value={`${info.term === '직접입력' ? info.term : `${info.term} 개월`}`}
                unit='개월'
                isInput={info.term === '직접입력'}
                inputName='termSelf'
                inputType='number'
                inputValue={info.termSelf}
                onClick={modalSwitching}
                onChange={onChange}
              />
            </Dd>
          </Item>
          <Item>
            <Dt>
              <Span fontSize='15px'>할부금리</Span>
            </Dt>
            <Dd>
              <Input
                type='number'
                name='percentage'
                width='40px'
                onChange={onChange}
                value={info.percentage}
              />
              <Span> %/년</Span>
            </Dd>
          </Item>
          <Item>
            <Dt>
              <Span fontSize='15px'>월납입금</Span>
            </Dt>
            <Dd>
              <Span fontWeight='500' fontSize='17px' color={ORANGE}>
                <CountNumber newNum={Math.round(info.monthlyPrice)} needComma />
              </Span>
              <Span fontWeight='500' fontSize='17px'>
                &nbsp;원
              </Span>
            </Dd>
          </Item>
        </Dl>
        <SimpleButton
          text='계산하기'
          onClick={calculate}
          color={WHITE}
          backgroundColor={LIGHT_BLUE}
          borderColor={LIGHT_BLUE}
          needHover
        />
        <SimpleButton
          text='초기화'
          onClick={init}
          color={LIGHT_BLUE}
          backgroundColor={WHITE}
          borderColor={LIGHT_BLUE}
          needHover
        />
      </Wrapper>
      {modalActive && (
        <SimpleListModal
          title='할부기간'
          list={TERM_LIST.map((term: number | string, index: number) =>
            transformObjToSimpleItem<number | string>({
              param: term,
              id: index + 1, // + 1 을 하는 이유는 배열은 0부터 시작하다보니 0의 값을 falsy한 값으로 받아들여져서 + 1 을 한다.
              name: term === '직접입력' ? term : `${term}개월`,
            })
          )}
          selectedNo={TERM_LIST.indexOf(info.term) + 1} // + 1 을 하는 이유는 배열은 0부터 시작하다보니 0의 값을 falsy한 값으로 받아들여져서 + 1 을 한다.
          active={modalActive}
          closeEvent={modalSwitching}
          onClick={onClick}
        />
      )}
      {alertActive && (
        <AlertModal
          active={alertActive}
          contents={
            (info.term === '직접입력' && !info.termSelf && '할부기간을 입력해주세요.') ||
            (!info.percentage && '할부금리를 입력해주세요.') ||
            ''
          }
          closeEvent={alertSwitching}
        />
      )}
    </>
  );
}

const INIT_STATE: InstallmentCalculator = {
  basePrice: 10000000,
  term: 3,
  termSelf: 0,
  percentage: 3,
  monthlyPrice: 0,
};

const TERM_LIST: (number | string)[] = [
  3,
  6,
  12,
  18,
  24,
  30,
  36,
  48,
  60,
  72,
  84,
  96,
  108,
  120,
  '직접입력',
];

export default Calculator;
